import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { ActionButton, BrandLoader, Dialog } from '@core/components';
import {
  type PrintHistoryBatch,
  getPrintHistory,
  getProductionItems,
} from '../../api/productionItems';

interface PrintHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  onReprint: (itemIds: number[]) => void;
}

const PrintHistoryDialog = ({
  open,
  onClose,
  onReprint,
}: PrintHistoryDialogProps) => {
  const [batches, setBatches] = useState<PrintHistoryBatch[]>([]);
  const [productionItemIds, setProductionItemIds] = useState<Set<number>>(
    new Set()
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [history, productionItems] = await Promise.all([
          getPrintHistory(),
          getProductionItems(),
        ]);
        if (!cancelled) {
          setBatches(history);
          setProductionItemIds(new Set(productionItems.map((item) => item.id)));
        }
      } catch {
        if (!cancelled) {
          toast.error('Failed to load print history');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      setBatches([]);
      setProductionItemIds(new Set());
      setLoading(false);
    };
  }, [open, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Print history"
      submitText="Close"
      onSubmitClick={onClose}
      hideCancel
      maxWidth="sm"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <BrandLoader size={50} />
        </Box>
      ) : batches.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No print history yet
        </Typography>
      ) : (
        <List disablePadding>
          {batches.map((batch) => (
            <ListItem
              key={batch.batchId}
              disableGutters
              secondaryAction={
                batch.itemIds.some((id) => productionItemIds.has(id)) ? (
                  <ActionButton
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onReprint(batch.itemIds);
                      onClose();
                    }}
                  >
                    Reprint
                  </ActionButton>
                ) : null
              }
              sx={{ alignItems: 'flex-start', py: 1.5 }}
            >
              <ListItemText
                primary={format(
                  parseISO(batch.printedAt),
                  'MMM d, yyyy h:mm a'
                )}
                secondary={[
                  `${batch.itemIds.length} ${batch.itemIds.length === 1 ? 'item' : 'items'}`,
                  batch.printedBy
                    ? [batch.printedBy.firstName, batch.printedBy.lastName]
                        .filter(Boolean)
                        .join(' ')
                    : 'Unknown',
                ].join(' · ')}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Dialog>
  );
};

export default PrintHistoryDialog;
